import * as React from 'react';
import { useLocation } from 'react-router-dom';

import {
  useAppFeedMatch,
  useChannelFeedMatch,
  useUserFeedMatch,
} from '@/components/AppHeader/hooks/useCurrentFeedOption';
import type { MetadataType } from '@/features/ChatBotV2/types';
import { usePreviewDocumentMatch } from '@/hooks/usePreviewDocumentMatch';

export const useGetSessionMetadata = (): MetadataType => {
  const appPagesMatch = useAppFeedMatch();
  const feedPagesMatch = useUserFeedMatch();
  const channelPagesMatch = useChannelFeedMatch();
  const previewPageDocumentMatch = usePreviewDocumentMatch();
  const location = useLocation();

  const metadata = React.useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const isDocPreviewOpen = searchParams.get('overlay-type') === 'preview';

    const metaData = {} as MetadataType;

    if (isDocPreviewOpen) {
      metaData.type = 'document';
      const channel_id = channelPagesMatch?.params.source;
      if (channel_id) {
        metaData.channel_id = channel_id;
      }
    } else if (previewPageDocumentMatch) {
      metaData.type = 'document';
      if (previewPageDocumentMatch?.channel_id) {
        metaData.channel_id = previewPageDocumentMatch?.channel_id;
      }
    } else if (appPagesMatch) {
      metaData.type = 'source';
      metaData.board_id = 'home';
      metaData.source = appPagesMatch?.params.source;
    } else if (channelPagesMatch) {
      metaData.type = 'channel';
      metaData.channel_id = channelPagesMatch?.params.source;
    } else if (feedPagesMatch) {
      const board_id = feedPagesMatch.params.source;
      metaData.type = 'feed';
      metaData.board_id = board_id;
    }
    return metaData;
  }, [
    appPagesMatch,
    channelPagesMatch,
    feedPagesMatch,
    previewPageDocumentMatch,
    location.search,
  ]);

  return metadata;
};

export const getRequestFieldsFromMetadata = (metadata: MetadataType) => {
  const fields = {} as Record<string, string | undefined>;
  switch (metadata.type) {
    case 'source':
      fields.source = metadata.source;
      fields.board_id = metadata.board_id;
      break;
    case 'channel':
      fields.channel_id = metadata.channel_id;
      fields.board_id = metadata.board_id;
      break;
    case 'feed':
      fields.board_id = metadata.board_id;
      break;
    case 'document':
      fields.document_id = metadata.document_id;
      fields.board_id = metadata.board_id;
      if (metadata.channel_id) {
        fields.channel_id = metadata.channel_id;
      }
      break;
  }
  return fields;
};
